import {
  marketingAgentPromptContextToolsSchema_,
  marketingAgentPromptGoalSchema_,
  marketingAgentPromptMethodSchema_,
  marketingAgentPromptScenarioSchema_,
  marketingAgentPromptSchema,
  marketingAgentPromptStrategySchema_,
  marketingAgentPromptTriggerSchema_,
  PromptCategory,
} from "@redotech/redo-marketing-templates-common/marketing/marketing-agent-prompt/marketing-agent-prompt-definition";
import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

/**
 * RPC schema for updating an existing marketing agent prompt.
 *
 * Reuses the Zod schemas from marketing-agent-prompt-definition.ts, making
 * fields optional for updates and requiring promptId.
 */

const baseUpdateSchema = z.object({
  promptId: zExt.objectId(),
  // Allow disabling/enabling explicitly
  disabledAt: z.date().nullish(),
});

// Omit system fields that shouldn't be updated directly or are handled by baseUpdateSchema
const omitSystemFields = {
  _id: true,
  createdAt: true,
  updatedAt: true,
  disabledAt: true,
} as const;

// Create update schemas by omitting system fields, making everything else partial,
// and merging with the required promptId. We must re-add promptCategory as a required
// literal to satisfy the discriminated union requirements.
const updateGoalInput = marketingAgentPromptGoalSchema_
  .omit(omitSystemFields)
  .partial()
  .merge(baseUpdateSchema)
  .extend({ promptCategory: z.literal(PromptCategory.GOAL) });

const updateMethodInput = marketingAgentPromptMethodSchema_
  .omit(omitSystemFields)
  .partial()
  .merge(baseUpdateSchema)
  .extend({ promptCategory: z.literal(PromptCategory.METHOD) });

const updateTriggerInput = marketingAgentPromptTriggerSchema_
  .omit(omitSystemFields)
  .partial()
  .merge(baseUpdateSchema)
  .extend({ promptCategory: z.literal(PromptCategory.TRIGGER) });

const updateStrategyInput = marketingAgentPromptStrategySchema_
  .omit(omitSystemFields)
  .partial()
  .merge(baseUpdateSchema)
  .extend({ promptCategory: z.literal(PromptCategory.STRATEGY) });

const updateScenarioInput = marketingAgentPromptScenarioSchema_
  .omit(omitSystemFields)
  .partial()
  .merge(baseUpdateSchema)
  .extend({ promptCategory: z.literal(PromptCategory.SCENARIO) });

const updateContextToolsInput = marketingAgentPromptContextToolsSchema_
  .omit(omitSystemFields)
  .partial()
  .merge(baseUpdateSchema)
  .extend({ promptCategory: z.literal(PromptCategory.CONTEXT_TOOLS) });

const updateMarketingAgentPromptInputSchema_ = z.discriminatedUnion(
  "promptCategory",
  [
    updateGoalInput,
    updateMethodInput,
    updateTriggerInput,
    updateStrategyInput,
    updateScenarioInput,
    updateContextToolsInput,
  ],
);

export type UpdateMarketingAgentPromptInput = z.infer<
  typeof updateMarketingAgentPromptInputSchema_
>;
export const updateMarketingAgentPromptInputSchema: z.ZodType<UpdateMarketingAgentPromptInput> =
  updateMarketingAgentPromptInputSchema_;

export const updateMarketingAgentPrompt = {
  name: "updateMarketingAgentPrompt" as const,
  input: updateMarketingAgentPromptInputSchema,
  output: z.object({
    success: z.boolean(),
    message: z.string().optional(),
    prompt: marketingAgentPromptSchema.optional(),
  }),
};
